import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchTourPackageById, clearError } from '../redux/slices/packageSlice';
import { fetchContactNumbers } from '../redux/slices/contactNumberSlice';
import ItineraryAccordion from './ItneraryAccordion';
import Header from './Header';
import ReviewComponent from './Reviews';
import LottiePlayer from './LottiePlayer';
import InclusionExclusion from './InclusionExclusion';
import KnowBeforeYouGo from './KnowYouBefore';

const TourPackageDetail = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const { contactNumbers } = useSelector((state) => state.contactNumbers);
    const { currentPackage, loading, error } = useSelector((state) => state.package);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalImages, setModalImages] = useState([]);
    const [selectedDurationIndex, setSelectedDurationIndex] = useState(0);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [id]);

    useEffect(() => {
        if (id) {
            dispatch(fetchTourPackageById(id));
        }
        return () => {
            dispatch(clearError());
        };
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(fetchContactNumbers());
    }, [dispatch]);

    const prevImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === 0 ? modalImages.length - 1 : prev - 1));
    }, [modalImages.length]);

    const nextImage = useCallback(() => {
        setCurrentImageIndex((prev) => (prev === modalImages.length - 1 ? 0 : prev + 1));
    }, [modalImages.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isModalOpen) return;
            if (e.key === 'ArrowLeft') {
                prevImage();
            } else if (e.key === 'ArrowRight') {
                nextImage();
            } else if (e.key === 'Escape') {
                closeModal();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, prevImage, nextImage]);

    const openModal = (index, images) => {
        setModalImages(images);
        setCurrentImageIndex(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalImages([]);
        setCurrentImageIndex(0);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <LottiePlayer
                    src="https://lottie.host/08969d3a-af8c-41f7-8b3e-86c73abeacc9/TnjpVnGl5h.lottie"
                    width={200}
                    height={200}
                    className="w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64"
                />
            </div>
        );
    }

    if (!currentPackage) {
        return <div className="text-center p-4 text-sm sm:text-base">No tour package found.</div>;
    }

    if (error) {
        return <div className="text-center p-4 text-red-500 text-sm sm:text-base">Error: {error}</div>;
    }

    const {
        title,
        originalPrice,
        discountedPrice,
        rating,
        reviewCount,
        duration,
        inclusions,
        images,
        durationOptions,
        tourType,
    } = currentPackage.tourPackage;

    const selectedDuration = durationOptions && durationOptions.length > 0
        ? durationOptions[selectedDurationIndex]
        : { days: '', originalPrice: '', discountedPrice: '', image: '', destinations: [], highlights: [], route: '', inclusionExclusions: [], knowBeforeYouGo: [], itineraries: [] };

    const { destinations, highlights, route, inclusionExclusions, knowBeforeYouGo, itineraries } = selectedDuration;

    const phoneNumber = contactNumbers[tourType]?.phone || '+916360204100';
    const whatsappNumber = contactNumbers[tourType]?.whatsapp || '+916360204100';

    const galleryImages = images && images.length > 0 ? images : [
        { url: 'https://images.unsplash.com/photo-1610106109549-5813ab87ae88?q=80&w=1974&auto=format&fit=crop', alt: 'Main tour image' },
        { url: 'https://images.unsplash.com/photo-1682687982183-c2937a74257c?q=80&w=2071&auto=format&fit=crop', alt: 'Destinations' },
        { url: 'https://images.unsplash.com/photo-1683009427666-340595e57e43?q=80&w=2070&auto=format&fit=crop', alt: 'Stays' },
        { url: 'https://images.unsplash.com/photo-1682687220742-aba13b6e50ba?q=80&w=2070&auto=format&fit=crop', alt: 'Activity & Sightseeing' },
        { url: 'https://images.unsplash.com/photo-1610106109549-5813ab87ae88?q=80&w=1974&auto=format&fit=crop', alt: 'View All Images' },
    ];

    return (
        <>
            <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 py-6 max-w-7xl mx-auto">
                <Header page="package-detail" />
                {/* Image Gallery Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-4">
                    <div className="sm:col-span-2 relative overflow-hidden">
                        <img
                            src={galleryImages[0].url}
                            alt={galleryImages[0].alt}
                            className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-lg sm:rounded-l-xl transition-transform duration-700 hover:scale-105 cursor-pointer"
                            onClick={() => openModal(0, galleryImages)}
                        />
                        <button
                            onClick={() => openModal(0, galleryImages)}
                            className="absolute bottom-4 left-2 flex items-center px-2 py-1 sm:px-3 sm:py-1.5 bg-gray-800 bg-opacity-50 text-white text-xs sm:text-sm rounded-full hover:bg-opacity-75"
                        >
                            <svg
                                className="w-4 h-4 sm:w-5 sm:h-5 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                                ></path>
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 21v-6a2 2 0 012-2h2a2 2 0 012 2v6"
                                ></path>
                            </svg>
                            View All ({galleryImages.length})
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        {galleryImages.slice(1, 5).map((item, index) => (
                            <div key={index} className="relative overflow-hidden">
                                <img
                                    src={item.url}
                                    alt={item.alt}
                                    className={`w-full h-32 sm:h-40 md:h-48 object-cover rounded-lg sm:rounded-none transition-transform duration-700 hover:scale-105 cursor-pointer
                                        ${index === 1 ? 'sm:rounded-tr-xl' : ''}
                                        ${index === 3 ? 'sm:rounded-br-xl' : ''}`}
                                    onClick={() => openModal(index + 1, galleryImages)}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Image Viewer Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-50">
                        <div className="relative w-full max-w-7xl h-[90vh] sm:h-[100vh] bg-black flex flex-col">
                            <button
                                onClick={closeModal}
                                className="absolute top-4 right-4 sm:top-8 sm:right-8 text-white hover:text-gray-300 z-50"
                            >
                                <svg
                                    className="w-6 h-6 sm:w-8 sm:h-8"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    ></path>
                                </svg>
                            </button>
                            <div className="relative flex-1 flex items-center justify-center">
                                <img
                                    src={modalImages[currentImageIndex].url}
                                    alt={modalImages[currentImageIndex].alt}
                                    className="w-full max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg"
                                />
                                <button
                                    onClick={prevImage}
                                    className="absolute left-2 sm:left-8 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                                >
                                    <svg
                                        className="w-6 h-6 sm:w-8 sm:h-8"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M15 19l-7-7 7-7"
                                        ></path>
                                    </svg>
                                </button>
                                <button
                                    onClick={nextImage}
                                    className="absolute right-2 sm:right-8 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                                >
                                    <svg
                                        className="w-6 h-6 sm:w-8 sm:h-8"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M9 5l7 7-7 7"
                                        ></path>
                                    </svg>
                                </button>
                            </div>
                            <div className="text-center text-white mt-2 mb-4 text-sm sm:text-base">
                                {currentImageIndex + 1} / {modalImages.length}
                            </div>
                            <div className="flex justify-center items-center gap-2 overflow-x-auto pb-4 px-2">
                                {modalImages.map((item, index) => (
                                    <img
                                        key={index}
                                        src={item.url}
                                        alt={item.alt}
                                        className={`w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg cursor-pointer ${currentImageIndex === index ? 'border-2 border-white' : ''}`}
                                        onClick={() => setCurrentImageIndex(index)}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 gap-4 mt-6">
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-custom-blue">{title}</h1>
                        <p className="text-gray-500 mt-1 sm:mt-2 text-sm sm:text-base line-through">₹ {selectedDuration.originalPrice}</p>
                        <p className="mt-1 sm:mt-2 text-custom-yellow text-sm sm:text-base">SAVE ₹ {selectedDuration.originalPrice - selectedDuration.discountedPrice}</p>
                        <p className="text-lg sm:text-xl md:text-2xl font-semibold text-custom-blue">
                            ₹ {selectedDuration.discountedPrice} <span className="text-xs sm:text-sm font-normal">Per Adult</span>
                        </p>
                    </div>
                    <div className="flex items-center">
                        <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 text-custom-yellow mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                        </svg>
                        <p className="text-base sm:text-lg font-semibold text-custom-blue">({rating}) ({reviewCount})</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <span className="bg-custom-yellow/20 text-custom-yellow px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">{duration}</span>
                        {destinations && destinations.length > 0 && destinations.map((destination, index) => (
                            destination?.trim() && (
                                <span key={index} className="border border-gray-300 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm text-custom-blue">
                                    {destination}
                                </span>
                            )
                        ))}
                    </div>

                    <hr className="bg-gray-300 h-0.5 mt-6 sm:mt-8 border-none" />

                    {/* Inclusions */}
                    <div className="flex flex-wrap gap-3 sm:gap-4 mt-4 text-gray-600">
                        {inclusions && inclusions.map((item, i) => {
                            const fileName = item.label.replace(/\s+/g, '-').toLowerCase() + '.svg';
                            return (
                                <div key={i} className="flex items-center w-full sm:w-1/2 md:w-1/4 lg:w-1/6">
                                    <img
                                        src={require(`../assets/icons/${fileName}`)}
                                        alt={`${item.label} icon`}
                                        className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                                        onError={(e) => { e.currentTarget.src = `${process.env.PUBLIC_URL}/icons/default-icon.svg`; }}
                                    />
                                    <span className="text-xs sm:text-sm">{item.label}</span>
                                </div>
                            );
                        })}
                    </div>

                    <hr className="bg-gray-300 h-0.5 mt-6 sm:mt-8 border-none" />

                    {/* Trip Duration Options */}
                    {durationOptions && durationOptions.length > 0 && (
                        <div className="mt-6">
                            <h2 className="text-base sm:text-lg font-semibold text-custom-blue">Choose Trip Duration</h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 mt-2">
                                {durationOptions.map((trip, index) => (
                                    <div
                                        key={index}
                                        className={`rounded-lg overflow-hidden shadow-sm cursor-pointer ${index === selectedDurationIndex ? 'border-2 border-custom-yellow' : 'border border-gray-300'}`}
                                        onClick={() => setSelectedDurationIndex(index)}
                                    >
                                        <div className="relative h-32 sm:h-40 w-full">
                                            <img
                                                src={trip.image}
                                                alt={`${trip.days} days`}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-custom-white text-custom-blue text-xs sm:text-sm font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded shadow">
                                                {trip.days} days
                                            </div>
                                        </div>
                                        <div className="text-center p-2 sm:p-3">
                                            <p className="text-gray-500 text-xs sm:text-sm">Starting From</p>
                                            <p className="font-semibold text-sm sm:text-base text-custom-blue">₹ {trip.discountedPrice}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Destination Route */}
                    {route?.trim() && (
                        <div className="mt-6">
                            <h2 className="text-base sm:text-lg font-semibold text-custom-blue">Destination Route</h2>
                            <div className="bg-custom-yellow/20 border border-custom-yellow/50 rounded-lg p-3 sm:p-4 mt-2">
                                <p className="text-custom-yellow font-semibold text-sm sm:text-base">{route}</p>
                            </div>
                        </div>
                    )}

                    <hr className="bg-gray-300 h-0.5 mt-6 sm:mt-8 border-none" />

                    {/* Trip Highlights */}
                    {highlights && highlights.length > 0 && (
                        <div className="bg-custom-white rounded-lg mt-6 text-left">
                            <h2 className="text-lg sm:text-xl font-semibold text-custom-blue mb-3 sm:mb-4">Trip Highlights</h2>
                            <ul className="list-disc pl-5 text-gray-600 space-y-1 sm:space-y-2 text-sm sm:text-base">
                                {highlights.map((highlight, index) => (
                                    highlight?.trim() && (
                                        <li className="font-bold" key={index}>{highlight}</li>
                                    )
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Itineraries */}
                    {itineraries && itineraries.length > 0 && (
                        <div className="mt-8 sm:mt-12">
                            {itineraries.map((itinerary, index) => (
                                itinerary.day?.trim() && itinerary.title?.trim() && itinerary.description?.trim() && (
                                    <ItineraryAccordion
                                        key={itinerary._id}
                                        day={itinerary.day}
                                        title={itinerary.title}
                                        description={itinerary.description}
                                        transferFrom={itinerary.sharedTransfer?.from}
                                        transferStop={itinerary.sharedTransfer?.stop}
                                        transferTo={itinerary.sharedTransfer?.to}
                                        checkInTime={itinerary.checkInTime}
                                        checkOutTime={itinerary.checkOutTime}
                                        hotels={itinerary.hotels}
                                        inclusions={itinerary.inclusions}
                                        activity={itinerary.activity}
                                    />
                                )
                            ))}
                        </div>
                    )}

                    {/* InclusionExclusion Component */}
                    {inclusionExclusions && inclusionExclusions.length > 0 && (
                        <div className="mt-6">
                            <InclusionExclusion
                                inclusions={inclusionExclusions.filter(item => item.category === 'inclusion' && item.label?.trim())}
                                exclusions={inclusionExclusions.filter(item => item.category === 'exclusion' && item.label?.trim())}
                            />
                        </div>
                    )}

                    {/* KnowBeforeYouGo Component */}
                    {knowBeforeYouGo && knowBeforeYouGo.length > 0 && (
                        <div className="mt-6">
                            <KnowBeforeYouGo tips={knowBeforeYouGo.filter(tip => tip.tip?.trim())} />
                        </div>
                    )}

                    {/* Call and WhatsApp Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3 mt-6">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                window.open(`tel:${phoneNumber}`);
                            }}
                            className="w-full border-custom-blue border-2 text-custom-blue py-2 sm:py-2.5 rounded-lg flex items-center justify-center text-sm sm:text-base hover:bg-custom-blue hover:text-white"
                        >
                            <img src="/calllogo.svg" alt="Call" className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                            Call Us Now
                        </button>
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                const detailLink = `${process.env.REACT_APP_BASE_URL}/package-detail/${id}`;
                                const message = `I'm interested in the tour package "${title}". More details: ${detailLink}`;
                                const encodedMessage = encodeURIComponent(message);
                                const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
                                window.open(whatsappUrl);
                            }}
                            className="sm:col-span-3 w-full bg-custom-yellow text-custom-blue py-2 sm:py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm sm:text-base hover:bg-custom-blue hover:text-white group"
                        >
                            <img
                                src="/whatsapp-blue.svg"
                                alt="WhatsApp"
                                className="w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-200 group-hover:filter group-hover:invert group-hover:brightness-0"
                            />
                            WhatsApp Us to Know More
                        </button>
                    </div>
                </div>
            </div>
            <ReviewComponent packageId={id} />
        </>
    );
};

export default TourPackageDetail;